import { useEffect, useState } from 'react';
import { DeviceSearchRow } from '../types';
import { deviceSearchWire, fetchDeviceSearch } from './useDeviceSearch';
import {
  DeviceListContext,
  readDeviceListView,
} from '../utils/deviceListContext';

/**
 * FS-542 — the devices either side of one position in a Devices-page list.
 *
 * Stepping walks positions, not pages: the request is a three-row window at
 * `offset = index - 1` over the list's own filters and sort, so the row after
 * the last one on a page is simply the next row — page boundaries never enter
 * into it. `total` comes back with the window, which is what the "device N of
 * M" readout needs.
 *
 * The row at the index is returned as `current` so the caller can check the
 * carried index still points at the device on screen. If the list moved under
 * the walk (a device added, filters edited in another tab), it won't, and the
 * caller drops the controls rather than stepping somewhere arbitrary.
 */
export interface DeviceListNeighbors {
  prev: DeviceSearchRow | null;
  /** The row the carried index points at — for the staleness check. */
  current: DeviceSearchRow | null;
  next: DeviceSearchRow | null;
  /** Devices in the whole list, not the window. 0 until the first response. */
  total: number;
  loading: boolean;
  error: string | null;
}

const EMPTY: DeviceListNeighbors = {
  prev: null,
  current: null,
  next: null,
  total: 0,
  loading: false,
  error: null,
};

interface NeighborState extends DeviceListNeighbors {
  /** Identifies the window this state describes; '' before any fetch. */
  key: string;
  /** List the total belongs to, so it survives a step within the same list. */
  query: string;
}

export function useDeviceListNeighbors(ctx: DeviceListContext | null): DeviceListNeighbors {
  const [state, setState] = useState<NeighborState>({ ...EMPTY, key: '', query: '' });

  const key = ctx ? `${ctx.index}\n${ctx.query}` : '';

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;

    // Window: the row before the index, the row at it, the row after. At the
    // head of the list there is no row before, so the window starts at 0 and
    // is one shorter.
    const offset = Math.max(ctx.index - 1, 0);
    const limit = ctx.index === 0 ? 2 : 3;
    const { params } = readDeviceListView(new URLSearchParams(ctx.query));
    const at = ctx.index === 0 ? 0 : 1;

    fetchDeviceSearch({ ...deviceSearchWire(params), limit, offset })
      .then((data) => {
        if (cancelled) return;
        const rows = data.devices ?? [];
        setState({
          key,
          query: ctx.query,
          prev: at === 1 ? rows[0] ?? null : null,
          current: rows[at] ?? null,
          next: rows[at + 1] ?? null,
          total: data.total ?? 0,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Device list neighbors lookup failed:', err);
        setState({ ...EMPTY, key, query: ctx.query, error: 'Failed to read the device list' });
      });

    return () => {
      cancelled = true;
    };
    // ctx is re-derived from the URL on every render; `key` is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!ctx) return EMPTY;

  // A window that hasn't arrived yet (first load, or the index just moved) is
  // reported as loading rather than as an empty list, so the caller doesn't
  // read "no next device" off a response for the previous position.
  if (state.key !== key) {
    return {
      ...EMPTY,
      // The list itself is unchanged when only the index moved — keep its size
      // so the "of M" readout doesn't blink between devices.
      total: state.query === ctx.query ? state.total : 0,
      loading: true,
    };
  }

  return state;
}
